import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Search, Trash2, RotateCcw, Car, User, AlertTriangle, CheckCircle,
  Plus, Pencil, Download, Upload, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    id_number: '',
    contact_number: '',
    employee_type: 'driver',
    owner_id: '',
    vehicle_registration: ''
  });
  
  // Import
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchEmployees();
    fetchOwners();
  }, [activeTab]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = activeTab === 'pending_deletion' ? '?status=pending_deletion' : 
                     activeTab === 'active' ? '?status=active' : '';
      const response = await axios.get(`${API_URL}/admin/employees${params}`);
      setEmployees(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load employees'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async () => {
    try {
      // Fetch all drivers (owner/drivers)
      const response = await axios.get(`${API_URL}/drivers`);
      setOwners(response.data || []);
    } catch (error) {
      console.error('Failed to fetch owners:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/admin/employees`, formData);
      toast.success('Employee created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create employee'));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/admin/employees/${selectedEmployee.id}`, formData);
      toast.success('Employee updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update employee'));
    }
  };

  const handleDelete = async () => {
    if (!employeeToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/admin/employees/${employeeToDelete.id}`);
      toast.success('Employee permanently deleted');
      setShowDeleteDialog(false);
      setEmployeeToDelete(null);
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete employee'));
    }
  };

  const handleRestore = async (employee) => {
    try {
      await axios.put(`${API_URL}/admin/employees/${employee.id}/restore`);
      toast.success('Employee restored');
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to restore employee'));
    }
  };

  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      full_name: employee.full_name || '',
      id_number: employee.id_number || '',
      contact_number: employee.contact_number || '',
      employee_type: employee.employee_type || 'driver',
      owner_id: employee.owner_id || '',
      vehicle_registration: employee.vehicle_registration || ''
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (employee) => {
    setEmployeeToDelete(employee);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      id_number: '',
      contact_number: '',
      employee_type: 'driver',
      owner_id: '',
      vehicle_registration: ''
    });
    setSelectedEmployee(null);
  };

  // Export employees to CSV
  const handleExport = () => {
    const headers = ['Type', 'Name', 'ID Number', 'Contact', 'Owner ID', 'Owner Name', 'Vehicle', 'Status'];
    const rows = filteredEmployees.map(emp => [
      emp.employee_type || '',
      emp.full_name || '',
      emp.id_number || '',
      emp.contact_number || '',
      emp.owner_id || '',
      emp.owner_name || '',
      emp.vehicle_registration || '',
      emp.status || 'active'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `employees_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Employees exported successfully');
  };

  // Import employees from CSV
  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API_URL}/admin/employees/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Import complete: ${response.data.imported} created, ${response.data.updated} updated`);
      setShowImportDialog(false);
      setImportFile(null);
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import employees'));
    } finally {
      setImportLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'pending_deletion':
        return <Badge className="bg-red-100 text-red-700">Pending Deletion</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    emp.owner_name?.toLowerCase().includes(search.toLowerCase()) ||
    emp.id_number?.includes(search)
  );

  const pendingCount = employees.filter(e => e.status === 'pending_deletion').length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-employees">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Employees Management</h1>
            <p className="text-muted-foreground mt-1">Manage all drivers and assistants linked to owners</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport} data-testid="export-employees">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-employees">
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="create-employee">
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
          </div>
        </div>

        {/* Alert for pending deletions */}
        {pendingCount > 0 && activeTab !== 'pending_deletion' && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">{pendingCount} employee(s) pending deletion</p>
              <p className="text-sm text-amber-700">Review and approve or reject deletion requests</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setActiveTab('pending_deletion')}
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
            >
              Review Now
            </Button>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
            <TabsTrigger value="pending_deletion" data-testid="tab-pending" className="relative">
              Pending Deletion
              {pendingCount > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, owner, or ID number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-employees-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Employees Table */}
        <Card>
          <CardHeader>
            <CardTitle>Employees</CardTitle>
            <CardDescription>
              {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">No employees found</h3>
                <p className="text-muted-foreground">
                  {activeTab === 'pending_deletion' 
                    ? 'No employees pending deletion' 
                    : 'No employees in this category'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>ID Number</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Owner/Driver</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEmployees.map((emp) => (
                      <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {emp.employee_type === 'driver' ? (
                              <><Car className="h-3 w-3 mr-1" />Driver</>
                            ) : (
                              <><User className="h-3 w-3 mr-1" />Assistant</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{emp.full_name}</TableCell>
                        <TableCell>{emp.id_number || '-'}</TableCell>
                        <TableCell>{emp.contact_number || '-'}</TableCell>
                        <TableCell className="text-blue-600 font-medium">{emp.owner_name || '-'}</TableCell>
                        <TableCell>{emp.vehicle_registration || '-'}</TableCell>
                        <TableCell>{getStatusBadge(emp.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {emp.status === 'pending_deletion' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestore(emp)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Restore employee"
                                data-testid={`restore-${emp.id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(emp)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Edit employee"
                              data-testid={`edit-${emp.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(emp)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete employee"
                              data-testid={`delete-${emp.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Employee Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
              <DialogDescription>
                Create a new driver or assistant and link them to an owner
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    data-testid="employee-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee_type">Employee Type *</Label>
                  <Select
                    value={formData.employee_type}
                    onValueChange={(value) => setFormData({ ...formData, employee_type: value })}
                  >
                    <SelectTrigger data-testid="employee-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="driver">Driver</SelectItem>
                      <SelectItem value="assistant">Assistant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_id">Link to Owner/Driver *</Label>
                  <Select
                    value={formData.owner_id}
                    onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
                  >
                    <SelectTrigger data-testid="owner-select">
                      <SelectValue placeholder="Select owner/driver" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map((owner) => {
                        const label = owner.driver_name || owner.owner_name || owner.full_name || owner.driver_username || 'Unnamed';
                        const idNo = owner.driver_id_number || owner.owner_id_number || owner.id_number;
                        return (
                          <SelectItem key={owner.id} value={owner.id}>
                            {label}{idNo ? ` (${idNo})` : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="id_number">ID Number</Label>
                  <Input
                    id="id_number"
                    value={formData.id_number}
                    onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                    data-testid="employee-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_number">Contact Number</Label>
                  <Input
                    id="contact_number"
                    value={formData.contact_number}
                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                    data-testid="employee-contact-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicle_registration">Vehicle Registration</Label>
                  <Input
                    id="vehicle_registration"
                    value={formData.vehicle_registration}
                    onChange={(e) => setFormData({ ...formData, vehicle_registration: e.target.value })}
                    placeholder="e.g., ABC 123 GP"
                    data-testid="employee-vehicle-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-employee-button">
                  Create Employee
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Employee Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Employee</DialogTitle>
              <DialogDescription>
                Update employee details and owner assignment
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEdit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_full_name">Full Name *</Label>
                  <Input
                    id="edit_full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_employee_type">Employee Type *</Label>
                  <Select
                    value={formData.employee_type}
                    onValueChange={(value) => setFormData({ ...formData, employee_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="driver">Driver</SelectItem>
                      <SelectItem value="assistant">Assistant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_owner_id">Link to Owner/Driver *</Label>
                  <Select
                    value={formData.owner_id}
                    onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner/driver" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map((owner) => {
                        const label = owner.driver_name || owner.owner_name || owner.full_name || owner.driver_username || 'Unnamed';
                        const idNo = owner.driver_id_number || owner.owner_id_number || owner.id_number;
                        return (
                          <SelectItem key={owner.id} value={owner.id}>
                            {label}{idNo ? ` (${idNo})` : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_id_number">ID Number</Label>
                  <Input
                    id="edit_id_number"
                    value={formData.id_number}
                    onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_contact_number">Contact Number</Label>
                  <Input
                    id="edit_contact_number"
                    value={formData.contact_number}
                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_vehicle_registration">Vehicle Registration</Label>
                  <Input
                    id="edit_vehicle_registration"
                    value={formData.vehicle_registration}
                    onChange={(e) => setFormData({ ...formData, vehicle_registration: e.target.value })}
                    placeholder="e.g., ABC 123 GP"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Update Employee
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete Employee
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to permanently delete this employee? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {employeeToDelete && (
              <div className="py-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="font-medium text-red-900">{employeeToDelete.full_name}</p>
                  <p className="text-sm text-red-700">
                    {employeeToDelete.employee_type === 'driver' ? 'Driver' : 'Assistant'}
                  </p>
                  {employeeToDelete.owner_name && (
                    <p className="text-sm text-red-600">Owner: {employeeToDelete.owner_name}</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setEmployeeToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="destructive"
                onClick={handleDelete}
                data-testid="confirm-delete-employee"
              >
                Delete Employee
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Employees</DialogTitle>
              <DialogDescription>
                Upload a CSV file to import employees. Required columns: Type, Name, ID Number, Contact, Owner ID, Vehicle
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleImport}>
              <div className="space-y-4 py-4">
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                  <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                    ref={fileInputRef}
                  />
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm font-medium">{importFile.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setImportFile(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Select CSV File
                    </Button>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-1">CSV Format:</p>
                  <code className="text-xs bg-gray-100 p-2 rounded block">
                    Type,Name,ID Number,Contact,Owner ID,Vehicle<br/>
                    driver,John Doe,8801015000085,0821234567,owner-id-here,ABC 123 GP
                  </code>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!importFile || importLoading}>
                  {importLoading ? 'Importing...' : 'Import'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
